import { type ReactNode, type SyntheticEvent, useCallback, useState } from 'react'

import { ButtonText } from '@/components/common/Button'
import { InputSelect } from '@/components/common/Input'
import { Icon } from '@/components/icons/Icon'
import { type DropdownOptionGroupType } from '@/defs/input'
import { useClickaway } from '@/hooks/useClickaway'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'

import styles from './FilterForm.module.scss'
import { FilterFormProvider } from './FilterFormProvider'

type Props<T extends string> = {
  children: ReactNode
  invalid?: boolean
  onDelete: () => void
  onSelectOption: (value: T) => void
  onSubmit: () => void
  optionGroups: DropdownOptionGroupType<T>[]
  value?: T
}

export const FilterForm = <T extends string>({
  children,
  invalid,
  onDelete,
  onSelectOption: onSelectOptionProp,
  onSubmit: onSubmitProp,
  optionGroups,
  value,
}: Props<T>) => {
  const [attempts, setAttempts] = useState(0)

  const onSubmit = useCallback(
    (e?: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      e?.preventDefault()

      if (invalid) {
        setAttempts((a) => a + 1)
        return
      }

      onSubmitProp()
    },
    [invalid, onSubmitProp],
  )

  const onSelectOption = useCallback(
    (option: T) => {
      setAttempts(0)
      onSelectOptionProp(option)
    },
    [onSelectOptionProp],
  )

  useClickaway({ ignore: 'filter-form', onClickAway: onSubmit })

  const optionDisplay = optionGroups.flatMap((g) => g.options).find((o) => o.value === value)?.label || (
    <p>-Select a filter type-</p>
  )

  return (
    <form data-attempts={attempts} onSubmit={onSubmit} {...getClickawayIgnoreProps(['filter-form'])}>
      <FilterFormProvider attempts={attempts} invalid={!!invalid}>
        <div className={styles.container}>
          <div className={styles.top}>
            <InputSelect onSelect={onSelectOption} optionGroups={optionGroups} value={value}>
              <div className={styles.optionDisplay}>
                {optionDisplay}
                <div className={styles.optionDisplayIcon}>
                  <Icon name='caret' />
                </div>
              </div>
            </InputSelect>
            <ButtonText aria-label='Delete Filter' hover='warning' onClick={onDelete}>
              <div className={styles.buttonDelete}>
                <Icon name='xMark' />
              </div>
            </ButtonText>
          </div>
          <div className={styles.form}>{children}</div>
        </div>
      </FilterFormProvider>
    </form>
  )
}
